"use client";

import { motion } from "framer-motion";
import { Calendar, Clock, Zap, ArrowRight, CircleCheck as CheckCircle2, Smartphone, Bot, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
export default function SchedulingAutomation() {
  const stats = [{
    label: "Lead Response Time",
    value: "< 15s"
  }, {
    label: "No-Show Reduction",
    value: "80%"
  }, {
    label: "Calendar Sync",
    value: "Real-Time"
  }, {
    label: "Automated Booking",
    value: "24/7"
  }];
  const pillars = [{
    number: "01",
    title: "AI Lead Pre-Qualification",
    description: "Filter out window-shoppers before they touch your schedule. AI verifies service area, job scope, and booking urgency.",
    icon: Bot,
    badge: "Intelligent Capture"
  }, {
    number: "02",
    title: "2-Way Calendar Sync",
    description: "Zero scheduling conflicts. Instantaneous bidirectional updates across Google Calendar, Outlook, and your CRM.",
    icon: Calendar,
    badge: "Zero Conflicts"
  }, {
    number: "03",
    title: "Automated Reminder Sequences",
    description: "Custom SMS & email confirmation sequences with 1-click rescheduling options to keep your calendar filled.",
    icon: Smartphone,
    badge: "Max Attendance"
  }];
  const bookingSteps = [{
    time: "09:00 AM",
    client: "Marcus Vance",
    service: "Commercial HVAC Inspection",
    status: "Confirmed via SMS",
    source: "AI Search Citation"
  }, {
    time: "10:30 AM",
    client: "Elena Rostova",
    service: "Residential System Audit",
    status: "Pre-Qualified",
    source: "ChatGPT Recommendation"
  }, {
    time: "01:15 PM",
    client: "David Sterling",
    service: "Emergency Repair Quote",
    status: "Deposit Paid",
    source: "Local GEO Query"
  }];
  return <section data-section-id="5162" id="schedulingautomation" className="bg-[hsl(240,10%,4%)] text-foreground py-20 md:py-28 lg:py-32 overflow-hidden border-b border-[hsl(240,5%,15%)]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Hero Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center mb-20 lg:mb-24">
          {/* Left Column: Messaging & CTAs */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col items-start">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[hsl(352,54%,33%)]/10 border border-[hsl(352,54%,33%)]/30 text-[hsl(352,54%,45%)] text-xs font-mono tracking-widest uppercase mb-6">
              <Zap className="w-3.5 h-3.5" />
              <span>System // Appointment Automation</span>
            </div>

            <h1 className="font-default text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground leading-[1.08] mb-6">
              Convert every AI search into a confirmed{" "}
              <span className="italic font-serif text-[hsl(352,54%,45%)] font-normal">
                appointment.
              </span>
            </h1>

            <p className="font-default text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl mb-8">
              Never lose a high-intent lead to a missed call or delayed response. Our automated scheduling engine qualifies leads instantly and fills your calendar 24/7.
            </p>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto mb-10">
              <Link to="/PricingPlansPage" className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-lg bg-[hsl(352,54%,33%)] text-white border border-[hsl(352,54%,33%)] hover:bg-[hsl(352,54%,40%)] hover:border-[hsl(352,54%,45%)] font-medium text-base transition-all duration-300 shadow-sm">
                <span>Get started now</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/VisibilityCheckupPage" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-lg bg-[hsl(240,10%,7%)] text-foreground border border-[hsl(240,5%,20%)] font-medium text-base hover:border-[hsl(352,54%,33%)] hover:text-white transition-all duration-300 shadow-sm">
                <span>Free Visibility Checkup</span>
              </Link>
            </div>

            {/* Live Metrics Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-6 border-t border-[hsl(240,5%,15%)] w-full">
              {stats.map((stat, i) => <div key={i} data-index={i} className="flex flex-col">
                  <span className="font-mono text-xl sm:text-2xl font-bold text-foreground">
                    {stat.value}
                  </span>
                  <span className="text-xs text-muted-foreground font-mono uppercase tracking-wider mt-1">
                    {stat.label}
                  </span>
                </div>)}
            </div>
          </motion.div>

          {/* Right Column: Visual Booking Command Dispatch UI */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7,
          ease: "easeOut",
          delay: 0.2
        }} className="lg:col-span-5">
            <div className="relative rounded-xl border border-[hsl(240,5%,18%)] bg-[hsl(240,10%,6%)] p-6 shadow-xl overflow-hidden">
              {/* Header Status */}
              <div className="flex items-center justify-between pb-4 mb-4 border-b border-[hsl(240,5%,15%)]">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-lg bg-[hsl(352,54%,45%)] animate-pulse" />
                  <span className="font-mono text-xs uppercase tracking-wider text-foreground font-semibold">
                    Live Dispatch Feed
                  </span>
                </div>
                <span className="font-mono text-[11px] px-2 py-0.5 rounded-lg bg-[hsl(240,5%,12%)] text-muted-foreground">
                  AI Sync Active
                </span>
              </div>

              {/* Booking Stream Items */}
              <div className="space-y-3.5 mb-6">
                {bookingSteps.map((item, idx) => <div key={idx} data-index={idx} className="p-3.5 rounded-lg bg-[hsl(240,10%,8%)] border border-[hsl(240,5%,14%)] hover:border-[hsl(352,54%,33%)]/50 transition-all duration-300">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-mono text-xs font-semibold text-[hsl(352,54%,45%)]">
                        {item.time}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] font-mono text-emerald-400 bg-emerald-950/40 px-2 py-0.5 rounded-lg border border-emerald-800/30">
                        <CheckCircle2 className="w-3 h-3" />
                        {item.status}
                      </span>
                    </div>
                    <div className="text-sm font-semibold text-foreground">
                      {item.client}
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
                      <span>{item.service}</span>
                      <span className="font-mono text-[10px] text-muted-foreground/80">
                        via {item.source}
                      </span>
                    </div>
                  </div>)}
              </div>

              {/* Automated Dispatch Notification Box */}
              <div className="p-4 rounded-lg bg-[hsl(352,54%,33%)]/10 border border-[hsl(352,54%,33%)]/30 flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-[hsl(352,54%,45%)] shrink-0 mt-0.5" />
                <div className="text-xs">
                  <p className="font-semibold text-foreground mb-0.5">
                    Automated Confirmation Sent
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    Client received SMS, calendar invite, and job scope details in 12 seconds with zero staff intervention.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Divider */}
        <div className="w-full h-px bg-[hsl(240,5%,15%)] mb-20" />

        {/* Pillars Section Title */}
        <div className="max-w-3xl mb-12">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[hsl(352,54%,45%)] block mb-3">
            Architected For High-Intent Conversion
          </span>
          <h2 className="font-default text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
            Built to turn search queries into scheduled revenue.
          </h2>
        </div>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pillars.map((pillar, i) => {
          const IconComponent = pillar.icon;
          return <motion.div key={pillar.number} data-index={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }}>
                <Card className="h-full bg-[hsl(240,10%,7%)] border border-[hsl(240,5%,15%)] hover:border-[hsl(352,54%,33%)]/60 rounded-xl p-8 transition-all duration-300 hover:-translate-y-1 shadow-md flex flex-col justify-between group">
                  <CardContent className="p-0 flex flex-col h-full">
                    <div className="flex items-center justify-between mb-6">
                      <span className="font-mono text-xs tracking-widest text-muted-foreground uppercase">
                        {pillar.number} // 03
                      </span>
                      <span className="text-[11px] font-mono px-2.5 py-1 rounded-lg bg-[hsl(240,10%,10%)] text-[hsl(352,54%,45%)] border border-[hsl(352,54%,33%)]/20">
                        {pillar.badge}
                      </span>
                    </div>

                    <div className="w-12 h-12 rounded-lg bg-[hsl(352,54%,33%)]/10 border border-[hsl(352,54%,33%)]/30 flex items-center justify-center mb-6 text-[hsl(352,54%,45%)] group-hover:scale-105 transition-transform duration-300">
                      <IconComponent className="w-6 h-6" strokeWidth={1.75} />
                    </div>

                    <h3 className="font-default text-xl font-bold text-foreground mb-3">
                      {pillar.title}
                    </h3>

                    <p className="font-default text-sm text-muted-foreground leading-relaxed mb-8 flex-grow">
                      {pillar.description}
                    </p>

                    <Link to="/ContactPage" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-foreground hover:text-[hsl(352,54%,45%)] transition-colors mt-auto">
                      <span>Explore Setup</span>
                      <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}